/**
 * @file components/form-templates/ProofreadDrawer.tsx
 * @description 表單校對窗口（2026-09-25 回報序42）。
 *
 * 表單每次更新後，於此逐條校對全部條文（段落標題、欄位、選項、表格欄、查核條文）：
 *  - 依段落（sections）分組顯示，編號沿用段落規則（查核條文用條文編號，其餘「段落序.欄位序」）；
 *  - 每條需勾選「已校對」，全部勾選後才可「確認校對無誤」；確認時可勾選發布通知給公司全體人員；
 *  - 發現錯漏可直接修改文字 →「儲存修正為新版本」（舊版本保留供既有表單渲染），再對新版本校對；
 *  - 或「標記待修正」留下紀錄（如需重新上傳原始檔）。
 * 每次確認/退回都寫入校對紀錄（校對人、時間、條文數、意見、通知人數）。
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Input,
  Progress,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { flattenSchemaForProofread, formatRevisionLabel, type ProofreadItem } from '@zhcb/shared';
import { message } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';
import type { FormTemplateVersionDto } from '@/components/form-templates/types';
import { color, text } from '@/styles/tokens';

/** 條文種類中文標籤 */
const KIND_LABEL: Record<ProofreadItem['kind'], string> = {
  'form-title': '表單標題',
  'section-title': '段落標題',
  'section-desc': '段落說明',
  'field-label': '欄位',
  option: '選項',
  column: '表格欄',
  'group-title': '條文大類',
  'group-subtitle': '條文小類',
  clause: '查核條文',
};

interface Props {
  /** 表單 ID */
  templateId: number;
  /** 表單名稱（標題用） */
  templateName: string;
  /** 待校對版本；null 時關閉 */
  version: FormTemplateVersionDto | null;
  /** 關閉 */
  onClose: () => void;
}

/** 表單校對抽屜 */
export function ProofreadDrawer({ templateId, templateName, version, onClose }: Props) {
  const qc = useQueryClient();
  const items = useMemo(
    () => (version ? flattenSchemaForProofread(version.schemaJson) : []),
    [version],
  );
  /** 已勾選的條文鍵 */
  const [checked, setChecked] = useState<Set<string>>(new Set());
  /** 修正內容：key → 新文字 */
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [notifyAll, setNotifyAll] = useState(true);
  const [onlyUnchecked, setOnlyUnchecked] = useState(false);

  const editCount = Object.keys(edits).length;
  const allChecked = items.length > 0 && items.every((i) => checked.has(i.key));

  /** 依段落分組（段落規則） */
  const groups = useMemo(() => {
    const map = new Map<number, { title: string; items: ProofreadItem[] }>();
    for (const it of items) {
      if (onlyUnchecked && checked.has(it.key)) continue;
      const g = map.get(it.sectionIndex) ?? { title: it.sectionTitle, items: [] };
      g.items.push(it);
      map.set(it.sectionIndex, g);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [items, checked, onlyUnchecked]);

  /** 失效相關查詢 */
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['form-templates'] });
    void qc.invalidateQueries({ queryKey: ['form-template-detail', templateId] });
  };

  /** 儲存修正為新版本 */
  const revise = useMutation({
    mutationFn: () =>
      apiClient.post<{ version: FormTemplateVersionDto }>(
        `/api/form-templates/${templateId}/versions/${version!.id}/revise`,
        { edits, changelog: notes.trim() || undefined },
      ),
    onSuccess: (res) => {
      message.success(`已建立新版本 v${res.version.version}，請對新版本重新校對`);
      invalidate();
      onClose();
    },
    onError: (err) => {
      console.error('revise form-template version failed', err);
      message.error(err instanceof Error ? err.message : '儲存修正失敗');
    },
  });

  /** 記錄校對結果 */
  const proofread = useMutation({
    mutationFn: (result: 'confirmed' | 'needs_fix') =>
      apiClient.post(`/api/form-templates/${templateId}/versions/${version!.id}/proofread`, {
        result,
        checkedKeys: [...checked],
        notes: notes.trim() || undefined,
        notify: result === 'confirmed' ? notifyAll : false,
      }),
    onSuccess: (_res, result) => {
      message.success(
        result === 'confirmed'
          ? notifyAll
            ? '已確認校對並發布通知給全體人員'
            : '已確認校對'
          : '已記錄「待修正」',
      );
      invalidate();
      onClose();
    },
    onError: (err) => {
      console.error('proofread form-template version failed', err);
      message.error(err instanceof Error ? err.message : '校對紀錄失敗');
    },
  });

  /** 切換單條勾選 */
  const toggle = (key: string, on: boolean) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const revText = version
    ? formatRevisionLabel({
        revisionLabel: version.revisionLabel,
        revisionDate: version.revisionDate,
        version: version.version,
      })
    : '';

  return (
    <Drawer
      open={version !== null}
      onClose={onClose}
      size="large"
      destroyOnHidden
      title={`校對：${templateName}（v${version?.version ?? ''} · ${revText}）`}
      footer={
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.TextArea
            rows={2}
            placeholder="校對意見 / 變更說明（選填，會寫入校對紀錄與通知內容）"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
          />
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <Space>
              <Switch checked={notifyAll} onChange={setNotifyAll} size="small" />
              <Typography.Text style={{ fontSize: text.sm }}>
                確認後發布通知給公司全體在職人員
              </Typography.Text>
            </Space>
            <Space wrap>
              <Button
                disabled={editCount === 0}
                loading={revise.isPending}
                onClick={() => revise.mutate()}
              >
                儲存修正為新版本（{editCount}）
              </Button>
              <Button
                danger
                loading={proofread.isPending && proofread.variables === 'needs_fix'}
                onClick={() => proofread.mutate('needs_fix')}
              >
                標記待修正
              </Button>
              <Button
                type="primary"
                disabled={!allChecked || editCount > 0}
                loading={proofread.isPending && proofread.variables === 'confirmed'}
                onClick={() => proofread.mutate('confirmed')}
              >
                確認校對無誤
              </Button>
            </Space>
          </Space>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          type="info"
          showIcon
          message="請逐條核對條文內容並勾選「已校對」；全部勾選後才可確認。發現錯漏可直接修改文字後「儲存修正為新版本」，舊版本會保留供既有表單使用。"
        />
        <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <Progress
              type="circle"
              size={36}
              percent={items.length ? Math.round((checked.size / items.length) * 100) : 0}
            />
            <Typography.Text>
              已校對 {checked.size} / {items.length} 條
              {editCount > 0 && `，待儲存修正 ${editCount} 處`}
            </Typography.Text>
          </Space>
          <Space>
            <Checkbox checked={onlyUnchecked} onChange={(e) => setOnlyUnchecked(e.target.checked)}>
              只看未校對
            </Checkbox>
            <Button size="small" onClick={() => setChecked(new Set(items.map((i) => i.key)))}>
              全部勾選
            </Button>
            <Button size="small" onClick={() => setChecked(new Set())}>
              清除勾選
            </Button>
          </Space>
        </Space>

        {items.length === 0 && <Alert type="warning" message="此版本沒有可校對的條文內容。" />}

        {groups.map(([idx, g]) => (
          <div key={idx}>
            <Typography.Title level={5} style={{ margin: '8px 0' }}>
              {idx >= 0 ? `段落 ${idx + 1}：${g.title}` : g.title}
            </Typography.Title>
            {g.items.map((it) => {
              const edited = edits[it.key] !== undefined;
              return (
                <div
                  key={it.key}
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    padding: '6px 0',
                    borderBottom: `1px solid ${color.border.subtle}`,
                  }}
                >
                  <Checkbox
                    checked={checked.has(it.key)}
                    onChange={(e) => toggle(it.key, e.target.checked)}
                    style={{ marginTop: 4 }}
                  />
                  <div style={{ width: 64, flexShrink: 0, fontSize: text.xs, paddingTop: 4 }}>
                    <div style={{ fontFamily: 'monospace' }}>{it.no}</div>
                    <Tag style={{ marginTop: 2, fontSize: 11 }}>{KIND_LABEL[it.kind]}</Tag>
                  </div>
                  <Input.TextArea
                    value={edits[it.key] ?? it.text}
                    autoSize={{ minRows: 1, maxRows: 8 }}
                    status={edited ? 'warning' : undefined}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEdits((prev) => {
                        const next = { ...prev };
                        if (v === it.text) delete next[it.key];
                        else next[it.key] = v;
                        return next;
                      });
                    }}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </Space>
    </Drawer>
  );
}
